import React, { useState, useEffect, useCallback } from 'react';
import { UserType, UserProfile } from '../../../types/userType';
import { fetchUserData } from '../../../api/auth';
import { fetchShippingInfo } from '../../../api/profileServices'; // Import the function
import './ProfileTab.css';
import iconSettings from '../../../assets/icons/icon_listheart.svg';
import iconFlyingsaucer from '../../../assets/icons/icon_flyingsaucer.svg';
import iconConfetti from '../../../assets/icons/icon_confetti.svg';
import SettingsModal from './SettingsModal';
import OnboardingCard from '../../common/OnboardingCard/OnboardingCard';
import ShippingInfoInputModal from '../../common/ShippingInfoInputModal/ShippingInfoInputModal';
import EditProfileModal from '../../common/EditProfileModal/EditProfileModal';
import VIToast from '../../common/VIToast/VIToast';
import { fetchPostsByUser } from '../../../api/userServices';
import Post from '../PlaygroundTab/Post/Post';
import { useNavigate } from 'react-router-dom';
import NewPostInputModal from '../PlaygroundTab/Post/NewPostInputModal';
import PostSkeleton from '../PlaygroundTab/Post/PostSkeleton';
import BrandBadgeIcon from '../../../assets/icons/icon_brand_badge.png';
import { updateProfilePicture } from '../../../api/userServices';
import TikTokAccountStatus from '../../common/TiktokAccountStatus/TikTokAccountStatus';

interface ShippingInfo {
  name: string;
  email: string;
  phoneNumber: string;
  addressLine1: string;
  addressLine2: string;
  country: string;
  state: string;
  city: string;
  zipcode: string;
}

interface ProfileTabProps {
  isActive: boolean;
  currentUser: UserType | null;
}

const ProfileTab: React.FC<ProfileTabProps> = ({ isActive, currentUser }) => {
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);
  const [hasValidShippingInfo, setHasValidShippingInfo] = useState(false); // New state
  const [shippingInfo, setShippingInfo] = useState<ShippingInfo | null>(null);
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userPosts, setUserPosts] = useState<any[]>([]); // Type this properly based on your PostProps
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [isNewPostModalOpen, setIsNewPostModalOpen] = useState(false);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previousImageUrl, setPreviousImageUrl] = useState<string | null>(null);
  const navigate = useNavigate();

  const mapApiResponseToShippingInfo = (apiData: any): ShippingInfo => {
    return {
      name: `${apiData.first_name} ${apiData.last_name}`,
      email: '', // API doesn't return email
      phoneNumber: apiData.phone_number,
      addressLine1: apiData.address_line_1,
      addressLine2: apiData.address_line_2,
      country: apiData.country,
      state: apiData.state,
      city: apiData.city,
      zipcode: apiData.postal_code
    };
  };

  const loadShippingInfo = async () => {
    try {
      const info = await fetchShippingInfo();
      if (info && Object.keys(info).length > 0) {
        setHasValidShippingInfo(true);
        const mappedInfo = mapApiResponseToShippingInfo(info);
        setShippingInfo(mappedInfo);
        localStorage.setItem('viUserShippingInfo', JSON.stringify(mappedInfo));
      } else {
        setHasValidShippingInfo(false);
        setShippingInfo(null);
        localStorage.removeItem('viUserShippingInfo');
      }
    } catch (error) {
      console.error('Error loading shipping info:', error);
      setHasValidShippingInfo(false);
      setShippingInfo(null);
      localStorage.removeItem('viUserShippingInfo');
    }
  };

  const loadUserPosts = async () => {
    try {
      setIsLoadingPosts(true);
      const postsData = await fetchPostsByUser(currentUser!.username);
      setUserPosts(postsData.results);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setIsLoadingPosts(false);
    }
  };

  const handleNewPostClick = () => {
    setIsNewPostModalOpen(true);
  };

  const handleNewPostModalClose = () => {
    setIsNewPostModalOpen(false);
    // Refresh posts when modal closes
    loadUserPosts();
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setPreviousImageUrl(previewUrl);
      setProfileImage(file);
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);

      try {
        await updateProfilePicture(currentUser!.username, file);
        setToastMessage('Profile picture updated successfully');
      } catch (error) {
        setToastMessage('Failed to update profile picture');
        setPreviewUrl(previousImageUrl);
      }
    }
  };

  const refreshUserData = async () => {
    try {
      setLoading(true);
      const freshUserData = await fetchUserData();
      setUser(freshUserData);
      setUserProfile({
        username: freshUserData.username,
        email: freshUserData.email || '',
        bio: freshUserData.bio || ''
      });
    } catch (error) {
      console.error('Error refreshing user data:', error);
      setError('Failed to refresh user data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isActive && currentUser) {
      // Refresh user data when tab becomes active
      refreshUserData();
      
      const loadData = async () => {
        try {
          await loadShippingInfo();
          await loadUserPosts();
        } catch (error) {
          console.error('Error loading data:', error);
        }
      };

      loadData();
    }
  }, [isActive, currentUser]);

  const toggleSettingsModal = () => {
    setIsSettingsModalOpen(!isSettingsModalOpen);
  };

  const toggleShippingModal = () => {
    setIsShippingModalOpen(!isShippingModalOpen);
    if (isShippingModalOpen) {
      loadShippingInfo();
    }
  };

  const toggleEditProfileModal = () => {
    setIsEditProfileModalOpen(!isEditProfileModalOpen);
  };

  const handleShippingInfoUpdate = async (success: boolean) => {
    if (success) {
      setToastMessage('Shipping information has been updated successfully');
      await loadShippingInfo();
    } else {
      setToastMessage('There was an error updating shipping information');
    }
  };

  const handleProfileUpdate = async (updatedProfile: UserProfile) => {
    setUserProfile(updatedProfile);
    setUser(prevUser => ({
      ...prevUser!,
      vivid_username: updatedProfile.username,
      email: updatedProfile.email,
      bio: updatedProfile.bio
    }));
    setToastMessage('Profile has been updated successfully');
  };

  const handlePostDeleted = (postId: number) => {
    setUserPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
  };

  const handleReplyClick = () => {
    console.log('Reply clicked');
  };

  const handlePostClick = useCallback((postSlug: string) => {
    navigate(`/posts/${postSlug}`);
  }, [navigate]);

  if (!isActive || !currentUser) return null;
  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  if (!user) return <div>No user data available</div>;

  return (
    <div className="profile-tab">
      <div className="top-bar">
        <button onClick={toggleSettingsModal} className="settings-button">
          <img src={iconSettings} alt="Settings" className="icon-settings" />
        </button>
      </div>
      <div className="profile-content">
        <div className="profile-header">
          <div className="profile-info">
            <h1 className="username">{user.username}</h1>
            {/* <div className="tiktok-status-container">
              <TikTokAccountStatus 
                verified={user?.tiktok_verified || false}
                numFollowers={ 0}
                isLoading={loading}
              />
            </div> */}
          </div>
          <div className="profile-avatar-container">
            <label htmlFor="profile-image-input" className="profile-image-container">
              <img 
                src={previewUrl || user.avatar} 
                alt={user.username} 
                className="profile-avatar" 
              />
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="image-upload"
              id="profile-image-input"
              style={{ display: 'none' }}
            />
            {user.account_type === 'brand' && (
              <img 
                src={BrandBadgeIcon} 
                alt="Brand" 
                className="brand-badge" 
              />
            )}
          </div>
        </div>
        <div className="profile-bio">
          <p>{user.bio || 'No bio available'}</p>
        </div>

        <div className="profile-actions">
          <button className="profile-action" onClick={toggleEditProfileModal}>Edit profile</button>
          <button className="profile-action" onClick={toggleShippingModal}>Shipping</button>
        </div>

        {/* Separator */}
        <div className="separator"></div>

        <div className="profile-cards">
          {!hasValidShippingInfo && (
            <OnboardingCard
              icon={iconFlyingsaucer}
              description1="Fill out your shipping details"
              description2="for more collab opportunities"
              buttonText="Shipping"
              onButtonClick={toggleShippingModal}
            />
          )}
          {userPosts.length === 0 && !isLoadingPosts && (
            <OnboardingCard
              icon={iconConfetti}
              description1="Start your first post"
              description2="with an awesome self-introduction"
              buttonText="New post"
              onButtonClick={handleNewPostClick}
            />
          )}
          {isLoadingPosts ? (
            <div className="user-posts">
              {[...Array(3)].map((_, index) => (
                <PostSkeleton key={index} />
              ))}
            </div>
          ) : userPosts.length > 0 ? (
            <div className="user-posts">
              {userPosts.map((post) => (
                <Post
                  key={post.id}
                  {...post}
                  onPostDeleted={handlePostDeleted}
                  currentUser={currentUser}
                  currentUserId={currentUser?.id?.toString() ?? ''}
                  isNewPost={false}
                  onReplyClick={handleReplyClick}
                  onClick={() => handlePostClick(post.slug)}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <SettingsModal isOpen={isSettingsModalOpen} onClose={toggleSettingsModal} />
      {isShippingModalOpen && (
        <ShippingInfoInputModal 
          isOpen={true} 
          onClose={toggleShippingModal} 
          initialShippingInfo={shippingInfo}
          onUpdateComplete={handleShippingInfoUpdate}
        />
      )}
      <EditProfileModal 
        isOpen={isEditProfileModalOpen} 
        onClose={toggleEditProfileModal} 
        onUpdateComplete={handleProfileUpdate}
        initialProfile={userProfile}
      />
      {isNewPostModalOpen && (
        <NewPostInputModal
          isOpen={true}
          onClose={handleNewPostModalClose}
          username={currentUser?.username || ''}
          avatarSrc={currentUser?.avatar|| ''}
          currentUser={currentUser!}
        />
      )}
      
      {toastMessage && (
        <VIToast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
};

export default ProfileTab;
